import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import type { MoveResponseDto } from '@/api/generated/api'
import { useNavigate } from 'react-router-dom'
import { User } from 'lucide-react'
import { sanitizeToPlainText } from '@/utils/sanitizeHtml'

interface MoveCardProps {
  move: MoveResponseDto
}

export function MoveCard({ move }: MoveCardProps) {
  const navigate = useNavigate()
  const [imageError, setImageError] = useState(false)
  const descriptionPreview = sanitizeToPlainText(move.description)

  const handleClick = () => {
    navigate(`/moves/${move.id}`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      navigate(`/moves/${move.id}`)
    }
  }

  const handleImageError = () => {
    setImageError(true)
  }

  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-all hover:shadow-md active:opacity-90"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${move.name}`}
    >
      <CardContent className="p-0">
        {/* Full-width thumbnail */}
        <div className="relative aspect-video w-full overflow-hidden bg-muted">
          {move.thumbnailUrl && !imageError ? (
            <img
              src={move.thumbnailUrl}
              alt={move.name}
              className="h-full w-full object-cover"
              onError={handleImageError}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-200">
              <User className="h-12 w-12 text-gray-400" />
            </div>
          )}
        </div>
        {/* Text content below thumbnail */}
        <div className="p-4">
          <h3 className="truncate text-base font-medium text-black transition-colors group-hover:text-[#0033A0]">
            {move.name}
          </h3>
          {descriptionPreview && (
            <p className="mt-1 truncate text-sm text-[#666666]">
              {descriptionPreview}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
